import { ApiError, apiBaseUrl } from "@tenergy/web-core/api";
import { randomNonce, sha256Hex, signWithBrowserKey } from "../auth/browser-key.js";
import { clearAdminSession, readAdminSession, renewAdminToken } from "../auth/session-store.js";
import { adminEnv } from "../env.js";

/**
 * The panel's transport (docs/product/admin-auth.md §4, §6). Nothing here is shared with the customer dashboard.
 *
 * EVERY CALL IS SIGNED. No cookie travels (`credentials: "omit"`); a call carries the admin session token and a
 * signature by this browser's non-extractable key over
 *
 *   METHOD \n /v1/path?query \n time(ms) \n nonce \n sha256hex(exact body)
 *
 * in X-Admin-Session / -Time / -Nonce / -Signature. The API refuses a call more than a minute off, a replayed nonce, or
 * a signature by any other key — so a token copied out of this browser opens nothing.
 *
 * A DANGEROUS ACTION answers 428 (`admin_confirmation_required`) with a text to sign. The registered confirm handler
 * (the confirmation dialog) gets it signed by the admin's confirmation key — on a Ledger, ideally — and the SAME call
 * is sent again with X-Admin-Confirm-Id / -Signature. One retry; a second 428 is an error.
 *
 * A failure keeps the API's REQUEST ID on the thrown `ApiError`, so a toast can say "validation_failed · req_01K…".
 */

export type AdminApiError = ApiError & { requestId: string | null };

/**
 * Which API a call goes to. Every ordinary screen is mainnet; the Nile page sets `nile` for its subtree while it is
 * mounted, so the same screens read the Nile API with the Nile session. A call reads the target synchronously, before
 * its first await, so a call already under way keeps the API it started with.
 */
export type ApiTarget = "mainnet" | "nile";

let activeTarget: ApiTarget = "mainnet";

export function setApiTarget(target: ApiTarget): void {
  activeTarget = target;
}

export interface ConfirmChallenge {
  confirmId: string;
  /** The exact text to sign; the device shows it (Ledger Ethereum app) or its hash (TRON). */
  message: string;
  keyAddress: string;
  keyKind: "tron" | "ethereum";
  safest: boolean;
  target: ApiTarget;
}

/** Returns the signature, or throws (the admin cancelled, the device refused). */
export type ConfirmHandler = (challenge: ConfirmChallenge) => Promise<string>;

let confirmHandler: ConfirmHandler | null = null;

export function setConfirmHandler(handler: ConfirmHandler | null): void {
  confirmHandler = handler;
}

/** The admin closed the confirmation dialog: nothing was sent a second time and nothing changed. */
export class ConfirmationCancelled extends Error {
  constructor() {
    super("Not confirmed: nothing was changed");
    this.name = "ConfirmationCancelled";
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  query?: Record<string, string | number | undefined>;
  body?: unknown;
  signal?: AbortSignal;
  /** Overrides the active target (a Growth page that reads both networks). */
  target?: ApiTarget;
  /** The sign-in calls: no session exists yet, so nothing is signed. */
  anonymous?: boolean;
}

function baseOf(target: ApiTarget): string {
  // Mainnet's base is the one main.tsx configured from env.ts (and the test setup overrides).
  return (target === "nile" ? adminEnv().nileApiBaseUrl : apiBaseUrl()).replace(/\/$/, "");
}

function buildUrl(base: string, path: string, query?: RequestOptions["query"]): URL {
  const url = new URL(`${base}${path}`);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }
  return url;
}

/**
 * The path and query the API will see, taken from the URL as it is sent. Every base ends in /v1 (env.ts), behind
 * whatever prefix the host adds (`/nile/v1` is the Nile proxy), and the API is mounted at /v1. Built from the URL and
 * never from the caller's `path`: a caller that writes its query into the path (`/providers/delivery?days=30`) once
 * had it signed twice, and the API refused every such call (2026-10-31).
 */
export function signedPath(url: URL, target: ApiTarget): string {
  const basePath = new URL(baseOf(target)).pathname.replace(/\/$/, "");
  return `/v1${url.pathname.slice(basePath.length)}${url.search}`;
}

function confirmChallengeOf(error: ApiError, target: ApiTarget): ConfirmChallenge | null {
  if (error.status !== 428 || error.slug !== "admin_confirmation_required") return null;
  const details = (error.details ?? {}) as Record<string, unknown>;
  if (typeof details["confirm_id"] !== "string" || typeof details["message"] !== "string") return null;
  return {
    confirmId: details["confirm_id"],
    message: details["message"],
    keyAddress: String(details["key_address"] ?? ""),
    keyKind: details["key_kind"] === "ethereum" ? "ethereum" : "tron",
    safest: details["safest"] === true,
    target,
  };
}

async function send(
  path: string,
  options: RequestOptions,
  target: ApiTarget,
  confirm: { id: string; signature: string } | null,
): Promise<{ response: Response; token: string | null }> {
  const method = options.method ?? "GET";
  const url = buildUrl(baseOf(target), path, options.query);
  const body = options.body === undefined ? "" : JSON.stringify(options.body);
  const headers: Record<string, string> = { Accept: "application/json" };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";

  let token: string | null = null;
  if (!options.anonymous) {
    const session = readAdminSession(target);
    if (session !== null) {
      token = session.token;
      const time = String(Date.now());
      const nonce = randomNonce();
      const text = [method, signedPath(url, target), time, nonce, await sha256Hex(body)].join("\n");
      headers["X-Admin-Session"] = session.token;
      headers["X-Admin-Time"] = time;
      headers["X-Admin-Nonce"] = nonce;
      headers["X-Admin-Signature"] = await signWithBrowserKey(target, text).catch(() => "");
    }
  }
  if (confirm !== null) {
    headers["X-Admin-Confirm-Id"] = confirm.id;
    headers["X-Admin-Confirm-Signature"] = confirm.signature;
  }

  const response = await fetch(url.toString(), {
    method,
    headers,
    credentials: "omit",
    ...(options.body === undefined ? {} : { body }),
    ...(options.signal ? { signal: options.signal } : {}),
  });
  return { response, token };
}

async function parsedBody(response: Response): Promise<unknown> {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return null;
  }
}

function errorOf(response: Response, parsed: unknown): AdminApiError {
  const fromBody = (parsed as { request_id?: unknown } | null)?.request_id;
  const requestId = typeof fromBody === "string" ? fromBody : response.headers.get("x-request-id");
  return new ApiError(response.status, parsed, requestId) as AdminApiError;
}

export async function adminRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const target = options.target ?? activeTarget;
  let confirm: { id: string; signature: string } | null = null;

  for (let round = 0; ; round += 1) {
    const { response, token } = await send(path, options, target, confirm);
    const renewed = response.headers.get("x-admin-session-renewed");
    if (renewed !== null && renewed !== "") renewAdminToken(target, renewed);
    const parsed = await parsedBody(response);
    if (response.ok) return parsed as T;
    const error = errorOf(response, parsed);

    if (response.status === 401 && error.slug === "admin_session_invalid" && !options.anonymous) {
      // The session ended (idle, 28 days, signed out elsewhere, frozen): back to the sign-in screen for this API —
      // unless another tab has signed in since, with a newer token this request did not carry.
      if (token !== null) clearAdminSession(target, token);
    }
    const challenge = round === 0 && confirmHandler !== null ? confirmChallengeOf(error, target) : null;
    if (challenge === null) throw error;
    let signature: string;
    try {
      signature = await confirmHandler!(challenge);
    } catch {
      throw new ConfirmationCancelled();
    }
    confirm = { id: challenge.confirmId, signature };
  }
}

/** The request id of a failure, when it carried one. */
export function requestIdOf(error: unknown): string | null {
  if (error === null || typeof error !== "object") return null;
  const value = (error as { requestId?: unknown }).requestId;
  return typeof value === "string" ? value : null;
}

/** The API's slug for a failure (`validation_failed`), or null for a network error. */
export function slugOf(error: unknown): string | null {
  return error instanceof ApiError ? (error.slug ?? null) : null;
}
